import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, Trash2, Save, PieChart as PieIcon, Layers, Share2 } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from "recharts";
import { AppShell } from "@/components/app/AppShell";
import { EquitySettingsCard } from "@/components/captable/EquitySettingsCard";
import { GrantLinkDialog } from "@/components/captable/GrantLinkDialog";
import { TeamEquityOverview } from "@/components/captable/TeamEquityOverview";
import { EquityQuestionsInbox } from "@/components/captable/EquityQuestionsInbox";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/DatePicker";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { formatCurrency } from "@/lib/format";

// Brand leads the categorical chart; additional hues keep ownership slices distinct.
const COLORS = ["hsl(var(--chart-1))", "hsl(var(--chart-2))", "#10B981", "#F59E0B", "#8B5CF6", "#EF4444", "#14B8A6", "#0EA5E9", "#64748B", "#334155"];
const EMPTY_SEC = { holder_name: "", holder_type: "founder", kind: "shares", class_id: "", shares: "", invested: "", strike_price: "", safe_amount: "", safe_cap: "", issued_on: "", holder_email: "", option_type: "ISO", vesting_start: "", vesting_months: 48, cliff_months: 12 };

const fmtShares = (n) => (n == null ? "-" : Number(n).toLocaleString());

export default function CapTable() {
  const { company } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pool, setPool] = useState("");
  const [classDialog, setClassDialog] = useState(false);
  const [classForm, setClassForm] = useState({ name: "", type: "common", authorized: "", price_per_share: "", liquidation_multiple: "" });
  const [secDialog, setSecDialog] = useState(false);
  const [secForm, setSecForm] = useState(EMPTY_SEC);
  const [saving, setSaving] = useState(false);
  const [grantDialog, setGrantDialog] = useState(null);

  const load = async () => {
    if (!company) return;
    try {
      const { data } = await api.get(`/companies/${company.id}/cap-table`);
      setData(data);
      setPool(data.summary.pool_size || "");
    } catch (e) { console.error("cap table load failed", e); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company]);

  const savePool = async () => {
    try {
      await api.put(`/companies/${company.id}/cap-table/pool`, { pool_size: pool === "" ? 0 : Number(pool) });
      toast.success("Option pool saved");
      load();
    } catch (err) { if (err?.response?.status !== 402) toast.error(err?.response?.data?.detail || "Could not save pool"); }
  };

  const addClass = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/companies/${company.id}/share-classes`, {
        ...classForm,
        authorized: classForm.authorized === "" ? null : Number(classForm.authorized),
        price_per_share: classForm.price_per_share === "" ? null : Number(classForm.price_per_share),
        liquidation_multiple: classForm.type === "preferred" && classForm.liquidation_multiple !== "" ? Number(classForm.liquidation_multiple) : null,
      });
      toast.success("Share class created");
      setClassDialog(false); setClassForm({ name: "", type: "common", authorized: "", price_per_share: "", liquidation_multiple: "" });
      load();
    } catch (err) { if (err?.response?.status !== 402) toast.error(err?.response?.data?.detail || "Could not create class"); }
    finally { setSaving(false); }
  };

  const addSecurity = async (e) => {
    e.preventDefault();
    setSaving(true);
    const num = (v) => (v === "" || v === undefined ? null : Number(v));
    try {
      await api.post(`/companies/${company.id}/securities`, {
        holder_name: secForm.holder_name, holder_type: secForm.holder_type, kind: secForm.kind,
        class_id: secForm.kind === "shares" ? secForm.class_id : null,
        shares: num(secForm.shares), invested: num(secForm.invested), strike_price: num(secForm.strike_price),
        safe_amount: num(secForm.safe_amount), safe_cap: num(secForm.safe_cap), issued_on: secForm.issued_on,
        ...(secForm.kind === "options" ? {
          holder_email: secForm.holder_email || null, option_type: secForm.option_type, vesting_start: secForm.vesting_start || null,
          vesting_months: num(secForm.vesting_months), cliff_months: num(secForm.cliff_months),
        } : {}),
      });
      toast.success("Security recorded");
      setSecDialog(false); setSecForm(EMPTY_SEC);
      load();
    } catch (err) { if (err?.response?.status !== 402) toast.error(err?.response?.data?.detail || "Could not record security"); }
    finally { setSaving(false); }
  };

  const removeSecurity = async (id) => {
    try { await api.delete(`/securities/${id}`); load(); }
    catch { toast.error("Delete failed"); }
  };

  const removeClass = async (id) => {
    try { await api.delete(`/share-classes/${id}`); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
  };

  const s = data?.summary;

  return (
    <AppShell title="Cap Table" subtitle="Share classes, securities and fully diluted ownership"
      actions={
        <div className="flex items-center gap-2">
          <Button variant="outline" className="gap-2" onClick={() => setClassDialog(true)} data-testid="class-add-btn"><Layers className="h-4 w-4" /> Share class</Button>
          <Button className="gap-2" onClick={() => setSecDialog(true)} data-testid="security-add-btn"><Plus className="h-4 w-4" /> Issue security</Button>
        </div>
      }>
      {loading ? <p className="text-sm text-muted-foreground">Loading…</p> : !data ? null : (
        <>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <Card className="rounded-xl border bg-card p-4"><div className="text-[11px] text-muted-foreground">Fully diluted shares</div><div className="h-display text-2xl font-semibold tabular-nums" data-testid="cap-fd">{fmtShares(s.fully_diluted)}</div></Card>
            <Card className="rounded-xl border bg-card p-4"><div className="text-[11px] text-muted-foreground">Shares outstanding</div><div className="h-display text-2xl font-semibold tabular-nums" data-testid="cap-outstanding">{fmtShares(s.outstanding)}</div></Card>
            <Card className="rounded-xl border bg-card p-4"><div className="text-[11px] text-muted-foreground">Options granted</div><div className="h-display text-2xl font-semibold tabular-nums" data-testid="cap-options">{fmtShares(s.options_granted)}</div></Card>
            <Card className="rounded-xl border bg-card p-4">
              <div className="text-[11px] text-muted-foreground">Option pool (authorized)</div>
              <div className="mt-1 flex gap-2">
                <Input type="number" value={pool} onChange={(e) => setPool(e.target.value)} className="h-8" data-testid="pool-input" />
                <Button size="sm" variant="outline" className="h-8 gap-1" onClick={savePool} data-testid="pool-save"><Save className="h-3.5 w-3.5" /></Button>
              </div>
              <div className="mt-1 text-[11px] text-muted-foreground">{fmtShares(s.pool_remaining)} unissued</div>
            </Card>
          </div>

          <div className="mt-6 space-y-6">
            <EquitySettingsCard companyId={company?.id} />
            <EquityQuestionsInbox companyId={company?.id} />
            <TeamEquityOverview companyId={company?.id} refreshKey={data?.securities?.length + ":" + (data?.securities || []).filter((s) => s.grant_token).length} onShare={(id) => { const sec = (data?.securities || []).find((s) => s.id === id); if (sec) setGrantDialog(sec); }} />
          </div>

          <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[1fr_360px]">
            <div className="space-y-6">
              <Card className="rounded-xl border bg-card p-5">
                <h2 className="mb-3 text-sm font-semibold text-foreground">Stakeholders (fully diluted)</h2>
                {data.stakeholders.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No securities issued yet. Create a share class, then issue shares.</p>
                ) : (
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead>Holder</TableHead><TableHead>Type</TableHead>
                      <TableHead className="text-right">Shares</TableHead><TableHead className="text-right">Options</TableHead>
                      <TableHead className="text-right">Invested</TableHead><TableHead className="text-right">FD %</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {data.stakeholders.map((r) => (
                        <TableRow key={r.holder_name} data-testid={`stakeholder-${r.holder_name.replace(/\s/g, "-")}`}>
                          <TableCell className="font-medium text-foreground">{r.holder_name}</TableCell>
                          <TableCell><Badge variant="secondary" className="font-normal capitalize">{r.holder_type}</Badge></TableCell>
                          <TableCell className="text-right tabular-nums">{fmtShares(r.shares)}</TableCell>
                          <TableCell className="text-right tabular-nums">{fmtShares(r.options)}</TableCell>
                          <TableCell className="text-right tabular-nums">{r.invested ? formatCurrency(r.invested) : "-"}</TableCell>
                          <TableCell className="text-right font-semibold tabular-nums text-primary">{r.fd_pct != null ? `${r.fd_pct}%` : "-"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </Card>

              <Card className="rounded-xl border bg-card p-5">
                <h2 className="mb-3 text-sm font-semibold text-foreground">Securities ledger</h2>
                {data.securities.length === 0 ? <p className="text-sm text-muted-foreground">Nothing issued yet.</p> : (
                  <div className="space-y-2">
                    {data.securities.map((sec) => (
                      <div key={sec.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border p-3 text-sm" data-testid={`security-row-${sec.id}`}>
                        <div className="min-w-0">
                          <span className="font-medium text-foreground">{sec.holder_name}</span>
                          <span className="ml-2 text-xs text-muted-foreground">
                            {sec.kind === "shares" && `${fmtShares(sec.shares)} shares · ${sec.class_name || "?"}`}
                            {sec.kind === "options" && `${fmtShares(sec.shares)} options${sec.strike_price ? ` @ $${sec.strike_price}` : ""}`}
                            {sec.kind === "safe" && `SAFE ${formatCurrency(sec.safe_amount)}${sec.safe_cap ? ` · cap ${formatCurrency(sec.safe_cap)}` : ""}`}
                            {sec.issued_on ? ` · ${sec.issued_on}` : ""}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          {sec.fd_pct != null && <span className="text-xs font-semibold tabular-nums text-primary">{sec.fd_pct}%</span>}
                          {sec.est_pct != null && <span className="text-xs tabular-nums text-amber-600">~{sec.est_pct}% as converted</span>}
                          {sec.kind === "options" && (
                            <Button variant={sec.grant_token ? "secondary" : "outline"} size="sm" className="h-7 gap-1 text-xs" onClick={() => setGrantDialog(sec)} data-testid={`grant-share-${sec.id}`}>
                              <Share2 className="h-3.5 w-3.5" /> {sec.grant_token ? "Shared" : "Share with employee"}
                            </Button>
                          )}
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-red-600" onClick={() => removeSecurity(sec.id)} data-testid={`security-delete-${sec.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </div>

            <div className="space-y-6">
              <Card className="rounded-xl border bg-card p-5">
                <h2 className="mb-1 flex items-center gap-2 text-sm font-semibold text-foreground"><PieIcon className="h-4 w-4 text-primary" /> Ownership</h2>
                {data.pie.length === 0 ? <p className="text-sm text-muted-foreground">Issue shares to see the breakdown.</p> : (
                  <div className="h-[260px]" data-testid="cap-pie">
                    <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 400, height: 260 }}>
                      <PieChart>
                        <Pie data={data.pie} dataKey="value" nameKey="name" innerRadius={55} outerRadius={85} paddingAngle={2}>
                          {data.pie.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                        </Pie>
                        <Tooltip formatter={(v) => `${v}%`} />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </Card>

              <Card className="rounded-xl border bg-card p-5">
                <h2 className="mb-3 text-sm font-semibold text-foreground">Share classes</h2>
                {data.classes.length === 0 ? <p className="text-sm text-muted-foreground">No classes yet (e.g. Common, Series Seed Preferred).</p> : (
                  <div className="space-y-2">
                    {data.classes.map((c) => (
                      <div key={c.id} className="flex items-center justify-between rounded-lg border p-3 text-sm" data-testid={`class-row-${c.id}`}>
                        <div>
                          <span className="font-medium text-foreground">{c.name}</span>
                          <span className="ml-2 text-xs capitalize text-muted-foreground">{c.type} · {fmtShares(c.issued)} issued{c.fd_pct != null ? ` · ${c.fd_pct}% FD` : ""}</span>
                        </div>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-red-600" onClick={() => removeClass(c.id)} data-testid={`class-delete-${c.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                      </div>
                    ))}
                  </div>
                )}
              </Card>

              <Card className="rounded-xl border bg-card p-5">
                <h2 className="mb-3 text-sm font-semibold text-foreground">Convertibles (not in FD count)</h2>
                {data.convertibles.length === 0 ? <p className="text-sm text-muted-foreground">No outstanding SAFEs or notes.</p> : (
                  <div className="space-y-2">
                    {data.convertibles.map((c) => (
                      <div key={c.id} className="rounded-lg bg-secondary/60 p-3 text-xs" data-testid={`convertible-${c.id}`}>
                        <span className="font-semibold text-foreground">{c.holder_name}</span> · {formatCurrency(c.safe_amount)}
                        {c.safe_cap ? ` at ${formatCurrency(c.safe_cap)} cap` : ""}{c.est_pct != null ? ` · ~${c.est_pct}% as converted` : ""}
                      </div>
                    ))}
                    <div className="text-[11px] text-muted-foreground">Total: {formatCurrency(s.safe_total)}. Model conversion in Rounds & Ownership.</div>
                  </div>
                )}
              </Card>
            </div>
          </div>
        </>
      )}

      <Dialog open={classDialog} onOpenChange={setClassDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>New share class</DialogTitle><DialogDescription>e.g. Common, Series Seed Preferred</DialogDescription></DialogHeader>
          <form onSubmit={addClass} className="space-y-3">
            <div><Label>Name</Label><Input value={classForm.name} onChange={(e) => setClassForm((f) => ({ ...f, name: e.target.value }))} required className="mt-1" data-testid="class-form-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Type</Label>
                <Select value={classForm.type} onValueChange={(v) => setClassForm((f) => ({ ...f, type: v }))}>
                  <SelectTrigger className="mt-1" data-testid="class-form-type"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="common">Common</SelectItem><SelectItem value="preferred">Preferred</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Authorized shares</Label><Input type="number" value={classForm.authorized} onChange={(e) => setClassForm((f) => ({ ...f, authorized: e.target.value }))} className="mt-1" data-testid="class-form-authorized" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Price per share ($)</Label><Input type="number" step="any" value={classForm.price_per_share} onChange={(e) => setClassForm((f) => ({ ...f, price_per_share: e.target.value }))} className="mt-1" data-testid="class-form-pps" /></div>
              {classForm.type === "preferred" && (
                <div><Label>Liquidation multiple</Label><Input type="number" step="any" placeholder="1" value={classForm.liquidation_multiple} onChange={(e) => setClassForm((f) => ({ ...f, liquidation_multiple: e.target.value }))} className="mt-1" data-testid="class-form-liq-multiple" /></div>
              )}
            </div>
            <Button type="submit" disabled={saving} className="w-full gap-2" data-testid="class-form-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Create class</Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={secDialog} onOpenChange={setSecDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Issue a security</DialogTitle><DialogDescription>Shares, option grants or SAFEs</DialogDescription></DialogHeader>
          <form onSubmit={addSecurity} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Holder</Label><Input value={secForm.holder_name} onChange={(e) => setSecForm((f) => ({ ...f, holder_name: e.target.value }))} required className="mt-1" data-testid="sec-form-holder" /></div>
              <div>
                <Label>Holder type</Label>
                <Select value={secForm.holder_type} onValueChange={(v) => setSecForm((f) => ({ ...f, holder_type: v }))}>
                  <SelectTrigger className="mt-1" data-testid="sec-form-holder-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{["founder", "investor", "employee", "advisor", "other"].map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Security kind</Label>
              <Select value={secForm.kind} onValueChange={(v) => setSecForm((f) => ({ ...f, kind: v }))}>
                <SelectTrigger className="mt-1" data-testid="sec-form-kind"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="shares">Shares</SelectItem><SelectItem value="options">Options</SelectItem><SelectItem value="safe">SAFE</SelectItem></SelectContent>
              </Select>
            </div>
            {secForm.kind === "shares" && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Share class</Label>
                  <Select value={secForm.class_id} onValueChange={(v) => setSecForm((f) => ({ ...f, class_id: v }))}>
                    <SelectTrigger className="mt-1" data-testid="sec-form-class"><SelectValue placeholder="Pick a class" /></SelectTrigger>
                    <SelectContent>{(data?.classes || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Shares</Label><Input type="number" value={secForm.shares} onChange={(e) => setSecForm((f) => ({ ...f, shares: e.target.value }))} className="mt-1" data-testid="sec-form-shares" /></div>
              </div>
            )}
            {secForm.kind === "options" && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Options</Label><Input type="number" value={secForm.shares} onChange={(e) => setSecForm((f) => ({ ...f, shares: e.target.value }))} className="mt-1" data-testid="sec-form-options" /></div>
                  <div><Label>Strike price ($)</Label><Input type="number" step="any" value={secForm.strike_price} onChange={(e) => setSecForm((f) => ({ ...f, strike_price: e.target.value }))} className="mt-1" data-testid="sec-form-strike" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Employee email (for their equity page)</Label><Input type="email" value={secForm.holder_email} onChange={(e) => setSecForm((f) => ({ ...f, holder_email: e.target.value }))} className="mt-1" data-testid="sec-form-holder-email" /></div>
                  <div>
                    <Label>Type</Label>
                    <Select value={secForm.option_type} onValueChange={(v) => setSecForm((f) => ({ ...f, option_type: v }))}>
                      <SelectTrigger className="mt-1" data-testid="sec-form-option-type"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="ISO">ISO</SelectItem><SelectItem value="NSO">NSO</SelectItem></SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div><Label>Vesting start</Label><DatePicker value={secForm.vesting_start} onChange={(v) => setSecForm((f) => ({ ...f, vesting_start: v }))} className="mt-1" data-testid="sec-form-vesting-start" /></div>
                  <div><Label>Vesting (mo)</Label><Input type="number" value={secForm.vesting_months} onChange={(e) => setSecForm((f) => ({ ...f, vesting_months: e.target.value }))} className="mt-1" data-testid="sec-form-vesting-months" /></div>
                  <div><Label>Cliff (mo)</Label><Input type="number" value={secForm.cliff_months} onChange={(e) => setSecForm((f) => ({ ...f, cliff_months: e.target.value }))} className="mt-1" data-testid="sec-form-cliff" /></div>
                </div>
              </>
            )}
            {secForm.kind === "safe" && (
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Amount ($)</Label><Input type="number" value={secForm.safe_amount} onChange={(e) => setSecForm((f) => ({ ...f, safe_amount: e.target.value }))} className="mt-1" data-testid="sec-form-safe-amount" /></div>
                <div><Label>Post-money cap ($)</Label><Input type="number" value={secForm.safe_cap} onChange={(e) => setSecForm((f) => ({ ...f, safe_cap: e.target.value }))} className="mt-1" data-testid="sec-form-safe-cap" /></div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Invested ($, optional)</Label><Input type="number" value={secForm.invested} onChange={(e) => setSecForm((f) => ({ ...f, invested: e.target.value }))} className="mt-1" data-testid="sec-form-invested" /></div>
              <div><Label>Issued on</Label><DatePicker value={secForm.issued_on} onChange={(v) => setSecForm((f) => ({ ...f, issued_on: v }))} className="mt-1" data-testid="sec-form-date" /></div>
            </div>
            <Button type="submit" disabled={saving} className="w-full gap-2" data-testid="sec-form-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Record security</Button>
          </form>
        </DialogContent>
      </Dialog>

      <GrantLinkDialog grant={grantDialog} open={!!grantDialog} onOpenChange={(o) => { if (!o) setGrantDialog(null); }} onChanged={load} />
    </AppShell>
  );
}
